import { getAllTagsDBCall } from "./db";

// =============================================================================
// TAG TYPES
// =============================================================================

/**
 * Single tag type with id and name
 */
export type SingleTag = {
  tag_id: number;
  tag_name: string;
};

/**
 * AllTags class that manages a mapping of tags
 */
export class AllTags {
  private static instance: AllTags;
  private tagsMapping: Map<number, string> = new Map();
  private isInitialized: boolean = false;

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): AllTags {
    if (!AllTags.instance) {
      AllTags.instance = new AllTags();
    }
    return AllTags.instance;
  }

  /**
   * Initialize the tags mapping (must be called before using other methods)
   */
  public async initialize(): Promise<void> {
    if (!this.isInitialized) {
      await this.init();
    }
  }

  /**
   * Initialize and populate the tags mapping from database
   */
  private async init(): Promise<void> {
    try {
      const tags = await getAllTagsDBCall();
      this.tagsMapping.clear();
      
      tags.forEach((tag: {id: number, name: string}) => {
        this.tagsMapping.set(tag.id, tag.name);
      });
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing tags mapping:', error);
      this.isInitialized = false;
    }
  }

  /**
   * Get the tags mapping
   */
  public getTagsMapping(): Map<number, string> {
    if (!this.isInitialized) {
      throw new Error('AllTags must be initialized before use. Call initialize() first.');
    }
    return this.tagsMapping;
  }

  /**
   * Get a tag name by id
   */
  public getTagName(tagId: number): string | undefined {
    if (!this.isInitialized) {
      throw new Error('AllTags must be initialized before use. Call initialize() first.');
    }
    return this.tagsMapping.get(tagId);
  }

  /**
   * Check if tags are initialized
   */
  public isTagsInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Refresh tags from database
   */
  public async refreshTags(): Promise<void> {
    await this.init();
  }

  /**
   * Get all tags as an array
   */
  public getAllTags(): SingleTag[] {
    if (!this.isInitialized) {
      throw new Error('AllTags must be initialized before use. Call initialize() first.');
    }
    const tags: SingleTag[] = [];
    this.tagsMapping.forEach((tag_name, tag_id) => {
      tags.push({ tag_id, tag_name });
    });
    return tags.sort((a, b) => a.tag_name.localeCompare(b.tag_name));
  }
}

// Export singleton instance
export const allTags = AllTags.getInstance();

